import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

const FIELDS = [
  { key: "handle_time_minutes", label: "Average human handle time (minutes)", hint: "Used for agent hours saved", step: "0.5" },
  { key: "reopen_window_days", label: "Reopen / repeat-contact window (days)", hint: "Same contact raising a new chat within this window counts as reopened", step: "1" },
  { key: "csat_target", label: "CSAT target (1–5)", hint: "Drawn as a target line on the CSAT trend", step: "0.1" },
  { key: "business_hours_start", label: "Business hours start (0–23)", hint: "For out-of-hours highlighting", step: "1" },
  { key: "business_hours_end", label: "Business hours end (1–24)", hint: "", step: "1" },
];

export const SettingsDialog = ({ open, onOpenChange, settings, onSaved }) => {
  const [form, setForm] = useState(settings || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open && settings) setForm(settings); }, [open, settings]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      FIELDS.forEach((f) => { body[f.key] = Number(body[f.key]); });
      await axios.put(`${API}/chatbot-metrics/settings`, body, { withCredentials: true });
      toast.success("Metrics settings saved");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      const field = Array.isArray(d) ? FIELDS.find((f) => d[0]?.loc?.includes(f.key))?.label : null;
      toast.error(typeof d === "string" ? d : field ? `Check "${field}": ${d[0].msg}` : "Please check the values and try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="metrics-settings-dialog">
        <DialogHeader>
          <DialogTitle>Chatbot Metrics settings</DialogTitle>
          <DialogDescription>These values change how the dashboard calculates its numbers.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {FIELDS.map((f) => (
            <div key={f.key} className="space-y-1">
              <Label htmlFor={f.key} className="text-sm">{f.label}</Label>
              <Input id={f.key} type="number" step={f.step} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`setting-${f.key}`} />
              {f.hint && <p className="text-xs text-slate-500">{f.hint}</p>}
            </div>
          ))}
          <div className="space-y-1">
            <Label htmlFor="timezone" className="text-sm">Timezone</Label>
            <Input id="timezone" value={form.timezone ?? ""} onChange={(e) => setForm({ ...form, timezone: e.target.value })} data-testid="setting-timezone" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="settings-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#425B76] hover:bg-[#344a61]" data-testid="settings-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
